import { Components, Inputs } from "@thisgorilla/primate-ds";
import { Dispatch, SetStateAction, useContext, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { CreatePostModalContext } from "../..";
import { useClickOutside } from "../../../../../../../../hooks/useClickOutside";
import { useAppSelector } from "../../../../../../../../redux";
import {
  FlexColumn,
  FlexRowSpaced,
  hideScrollbar,
} from "../../../../../../../Common/Styles";
import UserInfo from "../../../../../../../Common/UserInfo";
import { API_CALLS } from "../../../../../../../../API/api_calls";

const CollaboratorDropdown = ({
  setShowModal,
  setFilterText,
  filterText,
}: {
  setShowModal: Dispatch<SetStateAction<boolean>>;
  setFilterText: Dispatch<SetStateAction<string>>;
  filterText: string;
}) => {
  const { typography } = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const collaboratorListRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState("n");
  const { selectedCollaborator, setSelectedCollaborator } = useContext(
    CreatePostModalContext
  );
  const {
    collaboratorList,
    getCollaborators_API_STATUS,
    remainingCount,
    page,
  } = useAppSelector((state) => state.collaborator);

  useClickOutside({
    ref,
    setState: setShowModal,
  });

  return (
    <DropdownContainer
      ref={ref}
      $isCollaboratorAdded={selectedCollaborator.length > 0}
    >
      <Components.Menu itemGap={8} size="fill" withBackground>
        <AlignCenter>
          <Components.Text
            text={"Followers"}
            style={{ tokenStyle: typography.Heading.XS }}
            letterSpacing="letter-spacing-100"
            lineHeight="line-height-200"
            fontWeight="font-weight-semibold"
          />
          <Components.IconButton
            iconType="X"
            onClick={() => setShowModal(false)}
            kind="Tertiary"
            size="XS"
            disabled={false}
          />
        </AlignCenter>
        <Inputs.Search
          fieldName={"collaborator-search"}
          fieldType="Search"
          idPrefix={"collaborator-search"}
          placeholder="Search"
          handleSelect={() => {}}
          isRequired={false}
          onChangeHandler={(value) => {
            setSearchText(value);
          }}
          minimumCharCountBeforeSearch={-1}
          value={searchText}
          debounceTime={300}
          searchFunction={(value) => {
            setFilterText(value);
          }}
          iconClickHandler={() => {
            setFilterText("");
            setSearchText("");
          }}
          size="Small"
        />
        <CollaboratorList
          id="collaborator-list"
          ref={collaboratorListRef}
          onWheel={() => {
            const collaboratorListElement =
              collaboratorListRef.current as HTMLDivElement;
            const isApiCall =
              getCollaborators_API_STATUS !== "progress" &&
              remainingCount > 0 &&
              collaboratorListElement.scrollHeight -
                collaboratorListElement.clientHeight -
                collaboratorListElement.scrollTop <
                50;

            if (isApiCall) {
              API_CALLS.GLOBAL.searchCollaborators({
                query: filterText,
                page: page + 1,
              });
            }
          }}
        >
          {collaboratorList.map((collaborator, index) => (
            <Container
              key={index}
              onClick={() => {
                const collaboratorIndex = selectedCollaborator.findIndex(
                  (c) => c.id === collaborator.id
                );
                if (collaboratorIndex !== -1) {
                  setSelectedCollaborator(
                    selectedCollaborator.filter(
                      (user) => user.id !== collaborator.id
                    )
                  );
                } else {
                  setSelectedCollaborator([
                    ...selectedCollaborator,
                    collaborator,
                  ]);
                }
              }}
            >
              <UserInfo
                {...{
                  headline: collaborator.headline,
                  id: collaborator.id,
                  name: collaborator.first_name + " " + collaborator.last_name,
                  profile_picture: collaborator.profile_picture,
                }}
                showUserInfoOnHover={false}
                avatarSize="Small-40"
              />
              <Inputs.Checkbox
                checked={
                  selectedCollaborator.findIndex(
                    (user) => user.id === collaborator.id
                  ) !== -1
                }
                onChangeHandler={() => {}}
                label=""
              />
            </Container>
          ))}
        </CollaboratorList>
      </Components.Menu>
    </DropdownContainer>
  );
};

export default CollaboratorDropdown;

const DropdownContainer = styled.div<{ $isCollaboratorAdded: boolean }>`
  position: absolute;
  bottom: ${({ theme, $isCollaboratorAdded }) =>
    $isCollaboratorAdded
      ? theme.spacing["spacing-14"].value
      : theme.spacing["spacing-10"].value}rem;
  left: ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  width: 352px;
  transition: bottom 0.3s ease-in-out;
`;

const AlignCenter = styled(FlexRowSpaced)`
  align-items: center;
`;

const Container = styled(FlexRowSpaced)`
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  padding-left: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  align-items: center;
  cursor: pointer;

  &:focus,
  &:hover {
    background-color: ${({ theme }) =>
      theme.Color.background.neutral.subtle.default.value};
  }
`;

const CollaboratorList = styled(FlexColumn)`
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  height: 312px;
  overflow-y: auto;
  ${hideScrollbar}
`;
